'use client'

import { History, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import {
	clearRecentRoutes,
	formatRelative,
	loadRecentRoutes,
	type RecentRoute,
	shortWaypointLabel,
} from '@/lib/recent-routes'

/**
 * Recent-routes chip row.
 *
 * Surfaces the last 5 routes the user has calculated in this browser so
 * they can re-run a trip in one click. Renders only when:
 *   1. There is at least one recent route in localStorage, AND
 *   2. The form is currently "empty" (no origin or destination picked)
 *
 * Clicking a chip fills origin + destination + waypoints and triggers
 * Calculate via the onPick callback. The row reads once from storage on
 * mount — we don't need reactive updates because the caller remounts this
 * component whenever it shows.
 *
 * Visual: compact purple brand outlined chips that fit under the
 * destination input without dominating the form.
 */

interface RecentRoutesProps {
	/**
	 * Fired when a chip is clicked. Parent should load the full route
	 * (origin/dest/waypoints) into its store and trigger a calculation.
	 */
	onPick: (route: RecentRoute) => void
}

export function RecentRoutes({ onPick }: RecentRoutesProps) {
	const [routes, setRoutes] = useState<RecentRoute[]>([])

	useEffect(() => {
		setRoutes(loadRecentRoutes())
	}, [])

	if (routes.length === 0) return null

	const handleClear = () => {
		clearRecentRoutes()
		setRoutes([])
	}

	return (
		<div className="space-y-1.5">
			<div className="flex items-center justify-between">
				<span className="inline-flex items-center gap-1 text-[10px] font-medium uppercase tracking-wider text-text-muted">
					<History className="size-3" aria-hidden />
					Recent
				</span>
				<button
					type="button"
					onClick={handleClear}
					aria-label="Clear recent routes"
					className="text-[10px] text-text-muted hover:text-error inline-flex items-center gap-0.5"
					title="Clear all"
				>
					<X className="size-2.5" aria-hidden />
					Clear
				</button>
			</div>
			<div className="flex flex-wrap gap-1.5">
				{routes.map((r) => (
					<button
						key={r.calculatedAt}
						type="button"
						onClick={() => onPick(r)}
						title={`${r.origin.name} → ${r.destination.name} · ${formatRelative(r.calculatedAt)}`}
						className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-[11px] text-text-secondary transition-colors hover:border-brand/60 hover:text-text-primary"
					>
						<span className="font-medium">{shortWaypointLabel(r.origin)}</span>
						<span className="text-text-muted">→</span>
						<span className="font-medium">{shortWaypointLabel(r.destination)}</span>
						<span className="text-text-muted text-[9px]">· {formatRelative(r.calculatedAt)}</span>
					</button>
				))}
			</div>
		</div>
	)
}
